import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import SearchSidebar from './SearchSidebar';
import getOptimizedUrl from '../utils/imageUtils';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [user, setUser] = useState(null);
  const [isCollectionsOpen, setIsCollectionsOpen] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [bannerOffset, setBannerOffset] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const logoSrc = isHomePage ? '/images/Logo--White.svg' : '/nufab_black.png';
  const isActive = (path) => location.pathname === path;

  // Collections data - you can modify this array to change categories
  const collections = [
    {
      id: 1,
      name: 'Tops',
      number: '002',
      image: 'https://nufab-prod-images.s3.ap-south-1.amazonaws.com/products/Screenshot%202026-04-07%20184831-1775568076363-4fdfa83c82ea4d76.webp'
    },
    {
      id: 2,
      name: 'Bottoms',
      number: '003',
      image: 'https://nufab-prod-images.s3.ap-south-1.amazonaws.com/products/Ombre-midi-skirt-sangam-black-teal-2-1770845116514-78cc8367c9aa6bc9.webp'
    },
    {
      id: 3,
      name: 'Dresses',
      number: '004',
      image: 'https://nufab-prod-images.s3.ap-south-1.amazonaws.com/products/Mini-dress-sanchi-brick-red-brown-3-1770845164801-3faecc41abaa7ef2.webp'
    },
     {
      id: 4,
      name: 'Accessories',
      number: '005',
      image: 'https://nufab-prod-images.s3.ap-south-1.amazonaws.com/products/Stole-gulawat-indigo-0-1770845507882-7b1fe4fd14d001f0.webp'
    }
  ];

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, [location]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isCollectionsOpen) return;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyPosition = document.body.style.position;
    const prevBodyTop = document.body.style.top;
    const prevBodyLeft = document.body.style.left;
    const prevBodyRight = document.body.style.right;
    const prevBodyWidth = document.body.style.width;

    document.documentElement.style.setProperty('overflow', 'hidden', 'important');
    document.body.style.setProperty('overflow', 'hidden', 'important');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = prevBodyTop;
      document.body.style.left = prevBodyLeft;
      document.body.style.right = prevBodyRight;
      document.body.style.width = prevBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [isCollectionsOpen]);

  useEffect(() => {
    const updateBannerOffset = () => {
      const bannerEl = document.getElementById('site-banner');
      setBannerOffset(bannerEl ? bannerEl.offsetHeight : 0);
    };

    updateBannerOffset();
    window.addEventListener('resize', updateBannerOffset);
    window.addEventListener('banner:visibility', updateBannerOffset);

    const observer = new MutationObserver(updateBannerOffset);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      window.removeEventListener('resize', updateBannerOffset);
      window.removeEventListener('banner:visibility', updateBannerOffset);
      observer.disconnect();
    };
  }, []);

  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const loadCount = async () => {
      try {
        const user = localStorage.getItem('user');
        const token = localStorage.getItem('token');
        if (!user || !token) {
          setCartCount(0);
          return;
        }
        const { getCart } = await import('../services/cartService');
        const data = await getCart();
        if (Array.isArray(data)) setCartCount(data.reduce((s, i) => s + (i.qty || i.quantity || 0), 0));
      } catch (e) {
        setCartCount(0);
      }
    };

    loadCount();

    const onUpdated = () => loadCount();
    window.addEventListener('cart:updated', onUpdated);
    return () => { window.removeEventListener('cart:updated', onUpdated); };
  }, []);

  // Load wishlist count
  useEffect(() => {
    const loadWishlistCount = async () => {
      const user = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (user && token) {
        try {
          const { getWishlist } = await import('../services/wishlistService');
          const data = await getWishlist();
          setWishlistCount(data.length);
        } catch (error) {
          setWishlistCount(0);
        }
      } else {
        setWishlistCount(0);
      }
    };
    
    loadWishlistCount();
    
    const onWishlistUpdated = (e) => {
      if (e.detail?.wishlist) {
        setWishlistCount(e.detail.wishlist.length);
      } else {
        loadWishlistCount();
      }
    };
    
    const onAuthChange = () => loadWishlistCount();
    
    window.addEventListener('wishlist:updated', onWishlistUpdated);
    window.addEventListener('auth:login', onAuthChange);
    window.addEventListener('auth:logout', onAuthChange);
    
    return () => {
      window.removeEventListener('wishlist:updated', onWishlistUpdated);
      window.removeEventListener('auth:login', onAuthChange);
      window.removeEventListener('auth:logout', onAuthChange);
    };
  }, []);

  useEffect(() => {
    if (!isHomePage) return;

    const handleScroll = () => {
      // Get the mission statement section (the one with "We make everyday clothes...")
      const missionSection = document.querySelector('.bg-white.py-12');
      
      if (missionSection) {
        const missionRect = missionSection.getBoundingClientRect();
        // When the mission section reaches the top of the viewport (accounting for banner), make navbar sticky
        const bannerHeight = bannerOffset;
        if (missionRect.top <= bannerHeight) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage, bannerOffset]);

  return (
    <>
      {!(isHomePage && windowWidth < 1168) && (
        <div 
          className={`navbar-logo-center-home p-0 transition-all duration-300 ease-in-out ${isHomePage ? (isSticky ? 'fixed left-0 right-0 w-full z-[9999] bg-black' : 'absolute left-0 right-0 w-full z-[9999] bg-transparent') : 'relative z-50 bg-white'}`}
          style={isHomePage && !isSticky ? { 
            backgroundColor: 'transparent', 
            backgroundImage: 'none',
            top: `${bannerOffset}px`
          } : isHomePage && isSticky ? {
            top: '0px'
          } : {}}
        >
      <div 
        className={`navbar-logo-center-container-home w-nav px-0 transition-all duration-300 ease-in-out ${isHomePage ? (isSticky ? 'bg-black' : 'bg-transparent') : 'bg-white'}`}
        style={isHomePage && !isSticky ? { backgroundColor: 'transparent', backgroundImage: 'none' } : {}}
      >
        <div 
          className={`container-navbar-home px-0 max-w-full transition-all duration-300 ease-in-out ${isHomePage ? (isSticky ? 'bg-black' : 'bg-transparent') : 'bg-white'}`}
          style={isHomePage && !isSticky ? { backgroundColor: 'transparent', backgroundImage: 'none' } : {}}
        >
          <div className={`navbar-wrapper-home px-0 m-0 transition-all duration-300 ease-in-out ${isHomePage ? (isSticky ? 'bg-black' : 'bg-transparent') : 'bg-white'}`}
            style={isHomePage && !isSticky ? { backgroundColor: 'transparent', backgroundImage: 'none' } : {}}
          >
            <Link to="/" className={`navbar-logo-wrapper-home w-nav-brand ${isHomePage && !isSticky ? 'opacity-0' : 'opacity-100'}`}>
              <img width="122" loading="lazy" alt="Nufab Logo" src={logoSrc} />
            </Link>
            
            <nav role="navigation" className={`w-full justify-evenly  pl-0 ml-0 ${isMenuOpen ? 'w--open' : ''}`}>
              <div className="flex justify-between pl-0 ml-0">
                <ul className="nav-menu-block-home w-list-unstyled space-x-5 pl-0 ml-0 gap-0">
                  <li className="pl-0 ml-0">
                    <button 
                      onClick={() => window.dispatchEvent(new CustomEvent('search:toggle', { detail: { open: true } }))}
                      className={`nav-link w-inline-block no-underline bg-transparent border-0 cursor-pointer p-0 m-0 flex items-center`}
                    >
                      <div className="nav-icon_wrapper-home">
                        <img
                          src="/search.svg"
                          alt="Search"
                          width="28"
                          height="28"
                          style={{ filter: isHomePage ? 'invert(1)' : 'none' }}
                        />
                      </div>
                    </button>
                  </li>
                  <li>
                    <Link to="/about" className={`${!isHomePage ? 'text-black' : 'text-white'} font-semibold text-base hover:underline hover:underline-offset-8 hover:decoration-2 ${isActive('/about') ? 'underline underline-offset-8 decoration-2' : ''}`}>About Us</Link>
                  </li>
                  <li>
                    <Link to="/blogs" className={`${!isHomePage ? 'text-black' : 'text-white'} font-semibold text-base hover:underline hover:underline-offset-8 hover:decoration-2 ${isActive('/blogs') ? 'underline underline-offset-8 decoration-2' : ''}`}>Blog</Link>
                  </li>
                  <li>
                    <Link to="/photo-booth" className={`${!isHomePage ? 'text-black' : 'text-white'} font-semibold text-base hover:underline hover:underline-offset-8 hover:decoration-2 ${isActive('/photo-booth') ? 'underline underline-offset-8 decoration-2' : ''}`}>Album</Link>
                  </li>
                  <li>
                    <button
                      onClick={() => setIsCollectionsOpen(!isCollectionsOpen)}
                      className={`${!isHomePage ? 'text-black' : 'text-white'} font-semibold text-base hover:underline hover:underline-offset-8 hover:decoration-2 ${isActive('/store') ? 'underline underline-offset-8 decoration-2' : ''}`}
                    >
                      Store
                    </button>
                  </li>
                </ul>
                <div>
                  <ul className="nav-menu-block-home-2 nav_blck_gap w-list-unstyled">
                      <li>
                        <button onClick={() => window.dispatchEvent(new CustomEvent('wishlist:toggle', { detail: { open: true } }))} className={`${!isHomePage ? 'text-black' : 'text-white'} font-semibold text-base hover:underline hover:underline-offset-8 hover:decoration-2 flex items-center gap-2`}>
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 19 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z"
                              fill={!isHomePage ? 'white' : 'transparent'}
                              stroke={!isHomePage ? 'black' : 'white'}
                            />
                          </svg>
                          <span className="text-[0.9em]">{wishlistCount || 0}</span>
                        </button>
                      </li>
                      <li>
                        <button onClick={() => window.dispatchEvent(new CustomEvent('cart:toggle', { detail: { open: true } }))} className={`${!isHomePage ? 'text-black' : 'text-white'} font-semibold text-base hover:underline hover:underline-offset-8 hover:decoration-2 flex items-center gap-2`}>
                          Cart <span className="text-[0.9em]">{cartCount || 0}</span>
                        </button>
                      </li>
                    <li>
                      <button onClick={() => window.dispatchEvent(new CustomEvent('signup:toggle', { detail: { open: true } }))} className={`${!isHomePage ? 'text-black' : 'text-white'} font-semibold text-base hover:underline hover:underline-offset-8 hover:decoration-2`}>
                        <div className="nav-icon_wrapper-home relative flex items-center justify-center">
                          {user ? (
                            <>
                              <img 
                                loading="lazy" 
                                src={user.avatar ? getOptimizedUrl(user.avatar, 80) : '/profile.svg'} 
                                alt={user.name} 
                                className={user.avatar ? "nav-icon rounded-full w-5 h-5 object-cover" : "nav-icon w-5 h-5 object-contain shrink-0"} 
                                style={!user.avatar && isHomePage ? { filter: 'invert(1)' } : undefined}
                              />
                              {user.role === 'admin' && (
                                <span className="absolute -top-1 -right-1 bg-[#e74c3c] text-white text-[10px] px-1 py-0.5 rounded font-bold">
                                  A
                                </span>
                              )}
                            </>
                          ) : (
                            <img loading="lazy" src="/profile.svg" alt="User" className="nav-icon w-5 h-5 object-contain shrink-0" style={isHomePage ? { filter: 'invert(1)' } : undefined} />
                          )}
                        </div>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </nav>
            
            <div 
              className="menu-button-home w-nav-button"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <div className="nav-icon-home w-icon-nav-menu"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Sidebar Component */}
      <SearchSidebar />

      {/* Collections Dropdown */}
      {typeof document !== 'undefined' && createPortal(
      <>
      <div 
        className="fixed top-0 left-0 right-0 bg-white z-[20000] transition-transform duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] overflow-x-hidden overflow-y-auto max-h-[100vh]"
        style={{
          transform: isCollectionsOpen ? 'translateY(0)' : 'translateY(-100%)',
          boxShadow: isCollectionsOpen ? '0 4px 20px rgba(0, 0, 0, 0.1)' : 'none'
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-4 md:px-6 py-2">
          <div className="flex items-center">
            <img src='/nufab_black.png' alt="Nufab" className="w-28 md:w-32 lg:w-24 object-contain" />
          </div>
          <button 
            onClick={() => setIsCollectionsOpen(false)}
            className='cursor-pointer font-semibold text-black text-base md:text-lg'
           
          >
            Close
          </button>
        </div>
        
        <div className='flex flex-nowrap items-end gap-x-[clamp(8px,1.7vw,48px)] md:pb-3 sm:px-1.5 md:px-3 lg:px-4 overflow-hidden'>
          {/* All tile */}
          <div
            key="all"
            onClick={() => {
              setIsCollectionsOpen(false);
              navigate('/store');
            }}
            className="cursor-pointer flex min-w-0 items-end gap-0.5 sm:gap-1 md:gap-2 hover:opacity-95 transition-opacity duration-300 px-0.5"
          >
                <div className="w-28 md:w-32 lg:w-36 h-44 md:h-48 lg:h-52 bg-white overflow-hidden flex items-center justify-center shrink-0">
              <img 
                  src={'https://nufab-prod-images.s3.ap-south-1.amazonaws.com/products/roz%20roz%20cover-1770988570206-a042a987a279f5e1.webp'} 
                  alt={'All Collections'}
                  className="w-full h-full object-cover bg-white"
                />
            </div>
              <div className="pb-0 flex min-w-0 flex-col justify-between h-44 md:h-48 lg:h-52 pl-0.5 sm:pl-1 md:pl-1.5">
              <h2 className="m-0 text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-medium text-black">All</h2>
              <p className="m-0 text-xs md:text-sm text-gray-600">001</p>
            </div>
          </div>

          {collections.map((collection) => (
            <div 
              key={collection.id}
              onClick={() => {
                setIsCollectionsOpen(false);
                navigate(`/store?collection=${encodeURIComponent(collection.name)}`);
              }}
              className="cursor-pointer flex min-w-0 items-end gap-0.5 sm:gap-1 md:gap-2 hover:opacity-95 transition-opacity duration-300 px-0.5"
              >
              {/* Image */}
                  <div className="w-28 md:w-32 lg:w-36 h-44 md:h-48 lg:h-52 bg-transparent overflow-hidden flex items-center justify-center shrink-0">
                <img 
                  src={getOptimizedUrl(collection.image || '', 400)} 
                  alt={collection.name}
                  className="w-full h-full bg-white object-cover"
                />
              </div>
              
              {/* Text */}
              <div className="pb-0 flex min-w-0 flex-col justify-between h-44 md:h-48 lg:h-52 pl-0.5 sm:pl-1 md:pl-1.5">
                <h2 className="m-0 text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-medium text-black">
                  {collection.name === 'Accessories' ? 'Trinkets' : collection.name}
                </h2>
                <p className="m-0 text-xs md:text-sm text-gray-600">
                  {collection.number}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {/* <div className="px-4 md:px-5 pb-2 md:pb-0 flex justify-start text-black items-end">
          <p className="m-0 text-sm md:text-base lg:text-lg">
            © 2025 Nufab
          </p>
        </div> */}
      </div>

      {/* Collections Overlay */}
      {isCollectionsOpen && (
        <div 
          className="fixed inset-0 bg-[rgba(0,0,0,0.28)] z-[19999] transition-opacity duration-700 ease"
          onClick={() => setIsCollectionsOpen(false)}
        />
      )}
      </>,
      document.body
      )}

      {/* Spacer so page content starts below navbar on non-home pages */}
      {!isHomePage && (
        <div aria-hidden="true" className="hidden h-20 md:h-24 lg:h-28" />
      )}
        </div>
      )}

      {/* duplicate SearchSidebar removed */}
    </>
  );
};

export default Navbar;